import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Radio, Boxes, Rocket, Cpu, RefreshCw, Recycle, Zap, Play, Loader2,
  ArrowLeft, Send, Power, PowerOff,
} from "lucide-react";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Copilot Studio — bots + connectors, MS-Copilot-Studio-style.
// Landing: SVG node graph of six agents with their enterprise-system connectors.
// Click a node → Agent Builder panel: system prompt, connector toggles, live chat.

const AGENT_LAYOUT = [
  { id: "plan",    label: "Demand",      x: 90,   y: 220, icon: Radio,     caption: ["668 persona-specific", "requirements"], anchor: "left" },
  { id: "source",  label: "Supply",      x: 300,  y: 120, icon: Boxes,     caption: ["668 source allocations", "by persona + location"], anchor: "top" },
  { id: "deploy",  label: "Fulfilment",  x: 510,  y: 70,  icon: Rocket,    caption: ["request / PO / shipment", "+ Day-1 readiness"], anchor: "top" },
  { id: "operate", label: "Device",      x: 720,  y: 120, icon: Cpu,       caption: ["risk cohort +", "device evidence"], anchor: "top" },
  { id: "refresh", label: "Refresh",     x: 810,  y: 300, icon: RefreshCw, caption: ["extend / reuse /", "refurb / replace"], anchor: "right" },
  { id: "recover", label: "Recovery",    x: 510,  y: 380, icon: Recycle,   caption: ["redeployable supply", "+ disposition"], anchor: "bottom" },
];

// Shared lifecycle context card rendered at the ring's centre — mirrors
// the "SHARED LIFECYCLE CONTEXT" box in the HCL reference diagram.
const SHARED_CONTEXT_LINES = [
  "Employee · Persona · Requirement",
  "Source · PO · Shipment · Asset",
  "Experience · Action · Return",
];

// Applications-by-phase ribbon rendered below the graph.
const APPLICATIONS_BY_PHASE = [
  "Workday / HR",
  "ServiceNow HAM / ITSM",
  "WMS / 3PL",
  "Ariba / ERP",
  "OEMs",
  "DHL / UPS",
  "Intune / Jamf / Nexthink",
  "Blancco / ITAD",
];

// Enterprise-system nodes surrounding the agents, positioned around the ring.
const SYSTEM_LAYOUT = [
  { id: "workday",   label: "Workday",       x: 30,   y: 60,  agent: "plan"    },
  { id: "sap_hr",    label: "SAP HR",         x: 30,   y: 380, agent: "plan"    },
  { id: "sn_ham",    label: "SN HAM",         x: 170,  y: 40,  agent: "plan"    },
  { id: "nexthink",  label: "Nexthink DEX",   x: 720,  y: 220, agent: "operate" },
  { id: "ariba",     label: "SAP Ariba",      x: 300,  y: 20,  agent: "source"  },
  { id: "wms",       label: "WMS",            x: 440,  y: 20,  agent: "source"  },
  { id: "oem",       label: "Dell / HP / Lenovo OEM", x: 600, y: 20, agent: "source" },
  { id: "intune",    label: "Intune / Jamf",  x: 640,  y: 480, agent: "deploy"  },
  { id: "iam",       label: "IAM · Okta",     x: 470,  y: 500, agent: "deploy"  },
  { id: "carrier",   label: "UPS / DHL",      x: 300,  y: 480, agent: "deploy"  },
  { id: "itsm",      label: "SN ITSM",        x: 900,  y: 200, agent: "operate" },
  { id: "blancco",   label: "Blancco",        x: 730,  y: 460, agent: "recover" },
  { id: "itad",      label: "ITAD partner",   x: 340,  y: 500, agent: "recover" },
  { id: "finance",   label: "Finance · ERP",  x: 920,  y: 340, agent: "refresh" },
];

const AGENT_META = {
  plan:    { name: "Demand Intelligence Agent",    question: "What do we really need?",   sample: "How many devices does the EMEA December joiner wave really need?", connectors: ["workday", "sap_hr", "sn_ham"] },
  source:  { name: "Supply & Procurement Agent",   question: "How should we source it?",  sample: "Best sourcing plan for the 668 net devices in EMEA?",       connectors: ["ariba", "wms", "oem"] },
  deploy:  { name: "Fulfilment & Zero-Touch Agent",question: "Will everyone be ready?",   sample: "Are EMEA December joiners on track for Day-1 ready?", connectors: ["intune", "iam", "carrier"] },
  operate: { name: "Device Intelligence Agent",    question: "What is becoming risky?",   sample: "Which cohort has the lowest DEX this month?",        connectors: ["nexthink", "itsm"] },
  refresh: { name: "Refresh Decision Agent",       question: "What action per device?",   sample: "Which devices should I refresh vs extend on EMEA Refinery Field Line 3?", connectors: ["nexthink", "finance", "sn_ham"] },
  recover: { name: "Recovery & Circularity Agent", question: "What value can we retain?", sample: "Best action for LT-99321 (Alex Nguyen exit)?",       connectors: ["blancco", "itad", "sn_ham"] },
};

// Model / knobs shown in the builder (deterministic seed for the demo).
const AGENT_MODEL = "Flexspace Copilot · reasoning model";

function agentSystemPrompt(id) {
  const meta = AGENT_META[id];
  const stageDefs = {
    plan:    "Forecast net device demand by country, persona and model. Blend joiners, returns, refresh, retained-device signals and policy exclusions before Procurement raises a PO. Produce a qualified requirement, not a headcount number.",
    source:  "Federate stock across warehouses, returns pipeline and OEM. Apply legal-entity, tax, configuration, warranty and SLA constraints. Recommend the cheapest eligible sourcing plan — not the nearest inventory.",
    deploy:  "Coordinate employee + device + shipment + enrolment. Predict Day-1 risk. Choose the right remediation (address fix, IAM chase, Munich swap, fresh enrolment token) before the joiner arrives.",
    operate: "Turn endpoint signals (DEX + battery + patch + incidents + security + warranty) into proactive lifecycle action. Flag devices likely to disrupt users before they file a ticket.",
    refresh: "Decide EXTEND / REPAIR / RECOVER / REFRESH per device on condition and economics, not age alone. Feed refresh demand back to Supply and outgoing devices to Recovery with chain-of-custody.",
    recover: "Coordinate collect → wipe → grade → decide → HAM close. Match Grade-A returns to open Supply demand before sending to resale. Recovered supply feeds the next quarter's Supply plan.",
  };
  return (
    `You are the Flexspace ${meta.name}. ${stageDefs[id]}\n\n` +
    `Answer in 2–4 tight sentences grounded on the CONTEXT provided by upstream agents. ` +
    `Cite specific numbers when relevant. Never invent connector data — say "connector pending" if a source is disabled.`
  );
}

// -----------------------------------------------------------------------------
export default function Studio() {
  const { brand } = useBranding();
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState(null);
  const [story, setStory] = useState(null);
  const [selected, setSelected] = useState(null); // agent id
  const [hover, setHover] = useState(null);
  const [autoplay, setAutoplay] = useState({ running: false, active: null, edges: {}, log: [] });

  useEffect(() => {
    endpoints.connectorHealth().then((r) => setConnectors(r.connectors || [])).catch(() => setConnectors([]));
    endpoints.lifecycleStory().then(setStory).catch(() => {});
  }, []);

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  // Map connectors from health endpoint to our system ids (best-effort).
  const connectorLiveById = useMemo(() => {
    const m = {};
    (connectors || []).forEach((c) => {
      m[c.id] = c.live;
      if (c.id === "sap_hr")      m["sap_hr"]   = c.live;
      if (c.id === "intune_jamf") m["intune"]   = c.live;
      if (c.id === "comms_llm")   m["llm"]      = c.live;
    });
    m["sn_ham"] = m["sn_ham"] ?? m["servicenow"];
    m["itsm"]   = m["itsm"]   ?? m["servicenow"];
    return m;
  }, [connectors]);

  // ---------------- Autoplay: run through each agent ----------------
  const runAutoplay = async () => {
    if (autoplay.running || !story?.hops) return;
    setAutoplay({ running: true, active: null, edges: {}, log: [{ kind: "sys", text: "▶ Full lifecycle autoplay started · Flexspace Copilot" }] });
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    for (let i = 0; i < story.hops.length; i++) {
      const hop = story.hops[i];
      const c = hop.console || {};
      const stage = hop.stage;
      setAutoplay((a) => ({ ...a, active: stage, log: [...a.log, { kind: "agent", agent: hop.name, text: `── ${hop.num} · ${hop.name} — ${hop.framing}` }] }));
      // Stream reasoning steps
      for (const step of (c.reasoning || [])) {
        await sleep(320);
        setAutoplay((a) => ({ ...a, log: [...a.log, { kind: "step", agent: hop.name, text: step }] }));
      }
      // Land the decision
      if (c.decision) {
        await sleep(300);
        setAutoplay((a) => ({ ...a, log: [...a.log, { kind: "decision", agent: hop.name, text: `→ ${c.decision.equation} · ${c.decision.total_label}` }] }));
      }
      // Fire the outbound edge to the next agent
      const nextStage = c.handoff?.target;
      if (nextStage) {
        setAutoplay((a) => ({ ...a, edges: { ...a.edges, [stage]: true } }));
        await sleep(500);
      }
    }
    setAutoplay((a) => ({ ...a, running: false, log: [...a.log, { kind: "sys", text: "✓ Loop closed · Grade-A returns feed next Supply cycle" }] }));
  };

  return (
    <div className="max-w-[1240px] mx-auto" data-testid="studio-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-3" style={{ color: brandBlue }}>
            Copilot Studio
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-[1.15]">
            The bots and the connectors — one canvas.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl text-[15px] leading-7">
            Six agents · fourteen enterprise systems · Flexspace Copilot intelligence. Click any node to open its builder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="studio-pulse-btn"
            variant="outline"
            onClick={runAutoplay}
            disabled={autoplay.running || !story}
            className="h-11 px-4 border-slate-200"
          >
            {autoplay.running
              ? <Loader2 size={13} className="mr-1.5 animate-spin" style={{ color: brandBlue }} />
              : <Zap size={13} className="mr-1.5" style={{ color: brandBlue }} />}
            {autoplay.running ? "Running lifecycle…" : "Run full lifecycle"}
          </Button>
        </div>
      </div>

      {/* Node canvas */}
      <div
        className="rounded-2xl bg-white p-4 relative mb-6"
        style={{ border: `1px solid ${brandSoft}`, background: `radial-gradient(circle at 50% 40%, ${brandSoft}55 0%, #ffffff 60%)` }}
        data-testid="studio-canvas"
      >
        <svg viewBox="0 0 960 520" className="w-full h-[540px]">
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M0,0 L10,5 L0,10 Z" fill={brandBlue} />
            </marker>
          </defs>

          {/* Edges: agent ↔ its connectors */}
          {AGENT_LAYOUT.map((a) => {
            const meta = AGENT_META[a.id];
            return (meta.connectors || []).map((cid) => {
              const s = SYSTEM_LAYOUT.find((n) => n.id === cid);
              if (!s) return null;
              const live = connectorLiveById[cid];
              return (
                <line
                  key={`${a.id}-${cid}`}
                  x1={a.x} y1={a.y} x2={s.x} y2={s.y}
                  stroke={live ? "#10B981" : "#CBD5E1"}
                  strokeWidth={live ? 1.6 : 1}
                  strokeDasharray={live ? "" : "4 3"}
                  opacity={0.7}
                />
              );
            });
          })}

          {/* Ring edges: agent → agent (Demand → Supply → ... → Recovery → Demand) */}
          {AGENT_LAYOUT.map((a, i) => {
            const next = AGENT_LAYOUT[(i + 1) % AGENT_LAYOUT.length];
            const fired = autoplay.edges[a.id];
            const activeEdge = autoplay.active === a.id;
            return (
              <line
                key={`ring-${a.id}`}
                x1={a.x} y1={a.y} x2={next.x} y2={next.y}
                stroke={fired ? "#10B981" : brandBlue}
                strokeWidth={fired ? 2.6 : 2.2}
                opacity={fired ? 0.9 : (activeEdge ? 1 : 0.65)}
                markerEnd="url(#arrow)"
                strokeDasharray={activeEdge ? "6 4" : ""}
                style={{ animation: activeEdge ? "flow 900ms linear infinite" : "none", transition: "stroke 400ms ease" }}
              />
            );
          })}

          {/* System nodes */}
          {SYSTEM_LAYOUT.map((s) => {
            const live = connectorLiveById[s.id];
            return (
              <g key={s.id} data-testid={`studio-system-${s.id}`} style={{ cursor: "default" }}>
                <circle cx={s.x} cy={s.y} r={16} fill="#fff" stroke={live ? "#10B981" : "#94A3B8"} strokeWidth={live ? 2 : 1.2} />
                <text x={s.x} y={s.y + 4} textAnchor="middle" className="text-[9px] font-bold" fill={live ? "#0F766E" : "#475569"}>
                  {live ? "✓" : "·"}
                </text>
                <text x={s.x} y={s.y + 28} textAnchor="middle" className="text-[9px] font-semibold" fill="#334155">
                  {s.label}
                </text>
              </g>
            );
          })}

          {/* Agent nodes */}
          {AGENT_LAYOUT.map((a) => {
            const isSelected = selected === a.id;
            const isHover = hover === a.id;
            const isActive = autoplay.active === a.id;
            const passed = autoplay.edges[a.id];
            const r = isActive ? 38 : (isSelected || isHover ? 34 : 30);
            // Per-agent caption position: for the top-row nodes, drop the
            // caption just below the node (between the circle and the
            // central shared-context card — avoids the system-circle band
            // above y=40). For side / bottom nodes, standard below placement.
            const captionDy = r + 14;
            const captionAnchor = "middle";
            const captionColor = passed ? "#065F46" : (isActive ? "#0F172A" : "#475569");
            return (
              <g
                key={a.id}
                data-testid={`studio-agent-${a.id}`}
                onClick={() => setSelected(a.id)}
                onMouseEnter={() => setHover(a.id)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "pointer" }}
              >
                {isActive && (
                  <circle cx={a.x} cy={a.y} r={r + 8} fill="none" stroke={brandBlue} strokeWidth={2} opacity={0.35}>
                    <animate attributeName="r" from={r + 4} to={r + 14} dur="1.2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.6" to="0" dur="1.2s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle cx={a.x} cy={a.y} r={r} fill={passed ? "#10B981" : brandBlue} opacity={isSelected ? 1 : 0.94} style={{ transition: "fill 400ms ease, r 200ms ease" }} />
                <circle cx={a.x} cy={a.y} r={r} fill="none" stroke="#fff" strokeWidth={2} />
                <text x={a.x} y={a.y + 5} textAnchor="middle" className="text-[13px] font-bold" fill="#fff">
                  {a.label}
                </text>
                {/* Role caption · matches HCL reference diagram */}
                {a.caption && a.caption.map((line, li) => (
                  <text
                    key={li}
                    x={a.x}
                    y={a.y + captionDy + li * 12}
                    textAnchor={captionAnchor}
                    className="text-[10px] font-semibold"
                    fill={captionColor}
                    style={{ transition: "fill 400ms ease" }}
                    data-testid={`studio-caption-${a.id}-${li}`}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          })}

          {/* Shared Lifecycle Context — central card at the ring's centre.
              Mirrors the HCL reference diagram so an executive can see where
              the per-agent handoffs converge. Lights up (brand-blue fill)
              once the first autoplay edge fires. */}
          {(() => {
            const cx = 480, cy = 240, w = 220, h = 112;
            const anyPassed = Object.keys(autoplay.edges || {}).length > 0;
            return (
              <g data-testid="studio-shared-context">
                <rect
                  x={cx - w / 2}
                  y={cy - h / 2}
                  width={w}
                  height={h}
                  rx={10}
                  fill={anyPassed ? "#0F172A" : "#F8FAFC"}
                  stroke={anyPassed ? brandBlue : "#CBD5E1"}
                  strokeWidth={anyPassed ? 1.4 : 1}
                  style={{ transition: "fill 500ms ease, stroke 500ms ease" }}
                />
                <text
                  x={cx} y={cy - h / 2 + 22} textAnchor="middle"
                  className="text-[10px] font-bold" fill={anyPassed ? "#60A5FA" : brandBlue}
                  style={{ letterSpacing: "0.14em" }}
                >
                  SHARED LIFECYCLE CONTEXT
                </text>
                {SHARED_CONTEXT_LINES.map((line, i) => (
                  <text
                    key={i}
                    x={cx}
                    y={cy - h / 2 + 42 + i * 14}
                    textAnchor="middle"
                    className="text-[10.5px] font-semibold"
                    fill={anyPassed ? "#F8FAFC" : "#334155"}
                    style={{ transition: "fill 500ms ease" }}
                  >
                    {line}
                  </text>
                ))}
                <text
                  x={cx} y={cy + h / 2 - 10} textAnchor="middle"
                  className="text-[9px]"
                  fill={anyPassed ? "#94A3B8" : "#64748B"}
                >
                  Evidence + correlation IDs persist
                </text>
              </g>
            );
          })()}
        </svg>
        <style>{`@keyframes flow { to { stroke-dashoffset: -20; } }`}</style>

        {/* Legend */}
        <div className="absolute top-4 right-4 rounded-lg bg-white/80 border border-slate-200 p-2 text-[10px] flex flex-col gap-1 backdrop-blur">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#10B981" }} /> Live connector</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#CBD5E1" }} /> Sandbox / pending</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: brandBlue }} /> Agent handoff</div>
        </div>
      </div>

      {/* Applications invoked by phase ribbon — mirrors the HCL reference
          diagram's bottom band. Shows the ordered chain of enterprise systems
          the lifecycle touches, so an executive can read it like a sentence. */}
      <div
        className="rounded-xl px-5 py-3 mt-4"
        style={{ backgroundColor: brandSoft, border: `1px solid ${brandBlue}22` }}
        data-testid="studio-apps-ribbon"
      >
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold mb-2" style={{ color: brandBlue }}>
          Applications invoked by phase
        </div>
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap text-[11.5px] font-semibold text-slate-700">
          {APPLICATIONS_BY_PHASE.map((app, i) => (
            <span key={app} className="inline-flex items-center gap-2">
              <span>{app}</span>
              {i < APPLICATIONS_BY_PHASE.length - 1 && (
                <span className="text-slate-400" aria-hidden>→</span>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Mini reasoning console — populated by autoplay */}
      {(autoplay.log.length > 0 || autoplay.running) && (
        <StudioMiniConsole log={autoplay.log} running={autoplay.running} brandBlue={brandBlue} brandSoft={brandSoft} />
      )}

      {/* Builder */}
      {selected && (
        <AgentBuilder
          agentId={selected}
          onClose={() => setSelected(null)}
          connectorLiveById={connectorLiveById}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
        />
      )}

      {!selected && (
        <div className="rounded-2xl bg-white p-6 text-center text-sm text-slate-500 border border-slate-200">
          Click any agent bubble to open its <span className="font-semibold" style={{ color: brandBlue }}>Builder view</span> — system prompt, connectors and a live test chat.
        </div>
      )}
    </div>
  );
}

// ==================== Agent Builder ====================
function AgentBuilder({ agentId, onClose, connectorLiveById, brandBlue, brandSoft }) {
  const meta = AGENT_META[agentId];
  const [systemPrompt, setSystemPrompt] = useState(agentSystemPrompt(agentId));
  const [connectorState, setConnectorState] = useState(() => {
    // Default: reflect live/sandbox state from connectorLiveById
    return Object.fromEntries((meta.connectors || []).map((c) => [c, !!connectorLiveById[c]]));
  });
  const [question, setQuestion] = useState(meta.sample);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleConnector = (c) => setConnectorState((s) => ({ ...s, [c]: !s[c] }));

  const send = async () => {
    if (!question.trim() || busy) return;
    const q = question.trim();
    setMessages((m) => [...m, { role: "user", text: q }]);
    setQuestion("");
    setBusy(true);
    try {
      const res = await endpoints.lifecycleAsk({ stage: agentId, question: q });
      setMessages((m) => [...m, {
        role: "assistant",
        text: res.answer,
        metrics: res.metrics,
        sources: res.sources,
        provider: res.provider,
      }]);
    } catch (e) {
      toast.error("Agent didn't respond.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-6 md:p-8" style={{ border: `1px solid ${brandSoft}`, boxShadow: `0 1px 0 ${brandSoft}` }} data-testid={`builder-${agentId}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-1" style={{ color: brandBlue }}>
            {meta.question}
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">{meta.name}</h2>
          <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-2">
            <Sparkles size={11} style={{ color: brandBlue }} />
            {AGENT_MODEL}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onClose} className="h-9">
          <ArrowLeft size={12} className="mr-1.5" />
          Back to canvas
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* System prompt */}
        <div className="lg:col-span-5 rounded-xl border border-slate-200 p-4" data-testid="builder-prompt">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-2">System prompt</div>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            className="w-full h-52 text-[12px] font-mono resize-none rounded-md border border-slate-200 p-2 outline-none focus:border-slate-400"
          />
          <div className="text-[10px] text-slate-400 mt-1">Grounded on live context strip · Flexspace Copilot</div>
        </div>

        {/* Connectors */}
        <div className="lg:col-span-3 rounded-xl border border-slate-200 p-4" data-testid="builder-connectors">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-2">Connectors</div>
          <div className="space-y-1.5">
            {(meta.connectors || []).map((c) => {
              const enabled = connectorState[c];
              const live = connectorLiveById[c];
              const system = SYSTEM_LAYOUT.find((s) => s.id === c);
              return (
                <button
                  key={c}
                  onClick={() => toggleConnector(c)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-md border transition-colors"
                  style={{
                    backgroundColor: enabled ? brandSoft : "#F8FAFC",
                    borderColor: enabled ? brandBlue + "44" : "#E2E8F0",
                    color: enabled ? "#0F172A" : "#94A3B8",
                  }}
                  data-testid={`builder-connector-${c}`}
                >
                  {enabled ? <Power size={12} style={{ color: brandBlue }} /> : <PowerOff size={12} />}
                  <span className="text-[12px] font-semibold flex-1 text-left">{system?.label || c}</span>
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                    style={
                      live
                        ? { backgroundColor: "#ECFDF5", color: "#0F766E" }
                        : { backgroundColor: "#FEF3C7", color: "#B45309" }
                    }
                  >
                    {live ? "Live" : "Sandbox"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Test chat */}
        <div className="lg:col-span-4 rounded-xl border border-slate-200 p-4 flex flex-col" data-testid="builder-chat">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-2">Test chat</div>
          <div className="flex-1 min-h-[180px] max-h-[260px] overflow-y-auto space-y-2 mb-2 pr-1">
            {messages.length === 0 ? (
              <div className="text-[12px] text-slate-400 italic p-2">Send a prompt to test the agent's live response.</div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`rounded-md p-2 text-[12px] ${m.role === "user" ? "bg-slate-100" : ""}`}
                     style={m.role !== "user" ? { backgroundColor: brandSoft } : {}}>
                  <div className="text-[9px] uppercase font-bold mb-1" style={{ color: m.role === "user" ? "#64748B" : brandBlue }}>
                    {m.role === "user" ? "You" : "Agent"}
                  </div>
                  <div className="text-slate-800 leading-5 whitespace-pre-wrap">{m.text}</div>
                  {m.provider === "canned" && <div className="text-[9px] text-amber-700 mt-1">Fallback · LLM unavailable</div>}
                </div>
              ))
            )}
            {busy && (
              <div className="rounded-md p-2 text-[12px]" style={{ backgroundColor: brandSoft }}>
                <Loader2 size={12} className="animate-spin inline mr-1.5" style={{ color: brandBlue }} />
                <span className="text-slate-500">Streaming…</span>
              </div>
            )}
          </div>
          <div className="flex gap-1.5">
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
              placeholder={meta.sample}
              className="h-9 text-[12px]"
              data-testid="builder-chat-input"
            />
            <Button
              data-testid="builder-chat-send"
              disabled={busy || !question.trim()}
              onClick={send}
              className="h-9 px-3 text-white hover:opacity-90"
              style={{ backgroundColor: brandBlue }}
            >
              <Send size={12} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Mini reasoning console ====================
function StudioMiniConsole({ log, running, brandBlue, brandSoft }) {
  // Auto-scroll to bottom as new lines land.
  const containerId = "studio-mini-console-body";
  useEffect(() => {
    const el = document.getElementById(containerId);
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length]);

  return (
    <div
      className="rounded-2xl mb-6 overflow-hidden"
      style={{ backgroundColor: "#0F172A", border: `1px solid ${brandSoft}` }}
      data-testid="studio-mini-console"
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: running ? "#10B981" : "#64748B" }} />
          <div className="text-[11px] font-bold text-slate-300 uppercase tracking-widest">
            {running ? "Live · streaming reasoning" : "Reasoning trace"}
          </div>
        </div>
        <div className="text-[10px] text-slate-500 font-mono">Flexspace Copilot</div>
      </div>
      <div id={containerId} className="max-h-56 overflow-y-auto p-3 font-mono text-[11px] leading-5">
        {log.map((line, i) => (
          <div key={i} className="flex gap-2 py-0.5" style={{ animation: "consoleIn 200ms ease-out both" }}>
            <span className="text-slate-500 shrink-0 w-8">{String(i + 1).padStart(3, "0")}</span>
            {line.kind === "sys"      && <span className="font-semibold" style={{ color: "#38BDF8" }}>{line.text}</span>}
            {line.kind === "agent"    && <span className="font-semibold" style={{ color: "#FDE68A" }}>{line.text}</span>}
            {line.kind === "step"     && <span className="text-slate-300">{line.text}</span>}
            {line.kind === "decision" && <span className="font-semibold" style={{ color: "#86EFAC" }}>{line.text}</span>}
          </div>
        ))}
        {running && (
          <div className="flex gap-2 py-0.5">
            <span className="text-slate-500 shrink-0 w-8">…</span>
            <span className="inline-block w-1.5 h-3 animate-pulse" style={{ backgroundColor: "#38BDF8" }} />
          </div>
        )}
      </div>
      <style>{`@keyframes consoleIn { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

